import React from 'react';
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  Stack,
  Link,
  alpha,
} from '@mui/material';
import { CloseIcon, ContentCopyIcon } from '@/components/common/icons';
import { getWhatsAppShareConfig } from '@/services/firebase.config';
import { useSnackbar } from '@/providers';

interface ShareWhatsAppMessageDialogProps {
  open: boolean;
  onClose: () => void;
}

const ShareWhatsAppMessageDialog: React.FC<ShareWhatsAppMessageDialogProps> = ({
  open,
  onClose,
}) => {
  const { showSnackbar } = useSnackbar();

  // Get WhatsApp config from remote config
  const config = getWhatsAppShareConfig();
  const { title, subTitle, playstoreLink, iosLink, webUrl } = config;

  // Check if config is valid (webUrl is optional — keeps backwards compatibility).
  const isInvalid = !title || !subTitle || !playstoreLink || !iosLink;
  const errorMessage = 'Unable to fetch share message. Please try again later.';

  // Build copy message
  const copyMessage = isInvalid
    ? errorMessage
    : [
        `${title}`,
        '',
        `${subTitle}`,
        '',
        `Play Store: ${playstoreLink}`,
        `App Store: ${iosLink}`,
        webUrl ? `Web: ${webUrl}` : '',
      ]
        .filter(Boolean)
        .join('\n');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(copyMessage);
      showSnackbar('Copied to clipboard', 'success');
    } catch {
      showSnackbar('Failed to copy to clipboard', 'error');
    }
  };

  const handleLinkClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          position: 'fixed',
          right: 0,
          bottom: 20,
          top: 'auto',
          m: 0,
          width: 350,
          maxWidth: '95vw',
          borderRadius: '20px 0 0 20px',
          boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
          background: 'linear-gradient(135deg, #1E88E5 0%, #0C4A91 100%)',
          overflow: 'hidden',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      }}
    >
        <Box sx={{ p: 2.5 }}>
          {/* Header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: 'white' }}>
              Share Message
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              {!isInvalid && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                  onClick={handleCopy}
                  sx={{
                    textTransform: 'none',
                    bgcolor: 'white',
                    color: '#1E88E5',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    py: 0.5,
                    px: 1.5,
                    borderRadius: 1.5,
                    '&:hover': {
                      bgcolor: alpha('#fff', 0.9),
                    },
                  }}
                >
                  Copy
                </Button>
              )}
              <IconButton
                size="small"
                onClick={onClose}
                sx={{
                  color: 'white',
                  '&:hover': {
                    bgcolor: alpha('#fff', 0.1),
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Stack>
          </Stack>

          {/* Content */}
          {isInvalid ? (
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
              {errorMessage}
            </Typography>
          ) : (
            <Stack spacing={2}>
              {/* Title */}
              <Typography variant="caption" fontWeight={500} sx={{ color: 'white' }}>
                {title}
              </Typography>

              {/* Subtitle */}
              <Typography variant="caption" sx={{ color: 'white', fontWeight: 500 }}>
                {subTitle}
              </Typography>

              {/* Links Section */}
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ color: 'white', mb: 1.5 }}>
                  Download & Register Here
                </Typography>

                {/* Play Store Link */}
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" sx={{ color: 'white', display: 'block', mb: 0.5 }}>
                    Play Store link:
                  </Typography>
                  <Link
                    component="button"
                    onClick={() => handleLinkClick(playstoreLink)}
                    sx={{
                      color: 'white',
                      textDecoration: 'underline',
                      textDecorationThickness: '0.5px',
                      fontSize: '0.8rem',
                      wordBreak: 'break-all',
                      textAlign: 'left',
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.8,
                      },
                    }}
                  >
                    {playstoreLink}
                  </Link>
                </Box>

                {/* App Store Link */}
                <Box sx={{ mb: webUrl ? 1.5 : 0 }}>
                  <Typography variant="caption" sx={{ color: 'white', display: 'block', mb: 0.5 }}>
                    App Store link:
                  </Typography>
                  <Link
                    component="button"
                    onClick={() => handleLinkClick(iosLink)}
                    sx={{
                      color: 'white',
                      textDecoration: 'underline',
                      textDecorationThickness: '0.5px',
                      fontSize: '0.8rem',
                      wordBreak: 'break-all',
                      textAlign: 'left',
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.8,
                      },
                    }}
                  >
                    {iosLink}
                  </Link>
                </Box>

                {/* Candidate Web URL */}
                {webUrl && (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'white', display: 'block', mb: 0.5 }}>
                      Web link:
                    </Typography>
                    <Link
                      component="button"
                      onClick={() => handleLinkClick(webUrl)}
                      sx={{
                        color: 'white',
                        textDecoration: 'underline',
                        textDecorationThickness: '0.5px',
                        fontSize: '0.8rem',
                        wordBreak: 'break-all',
                        textAlign: 'left',
                        cursor: 'pointer',
                        '&:hover': {
                          opacity: 0.8,
                        },
                      }}
                    >
                      {webUrl}
                    </Link>
                  </Box>
                )}
              </Box>
            </Stack>
          )}
        </Box>
    </Dialog>
  );
};

export default ShareWhatsAppMessageDialog;
